import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { StaticPage } from '../api/types';

interface PageModalProps {
    slug: string;
    onClose: () => void;
}

/** Modal showing a static page (FAQ, privacy policy, terms). */
export function PageModal({ slug, onClose }: PageModalProps) {
    const [page, setPage] = useState<StaticPage | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setPage(null);
        setError(null);

        api.page(slug)
            .then((loaded) => !cancelled && setPage(loaded))
            .catch((err: unknown) =>
                !cancelled && setError(err instanceof Error ? err.message : 'The page could not be loaded'),
            );

        return () => {
            cancelled = true;
        };
    }, [slug]);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="modal__panel" onClick={(event) => event.stopPropagation()}>
                <header className="modal__head">
                    <h2>{page?.title ?? 'Loading…'}</h2>
                    <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </header>

                <div className="modal__body">
                    {error && <p className="alert alert--error">{error}</p>}
                    {!error && !page && <p className="modal__loading">Loading…</p>}
                    {page && (
                        // The HTML comes from our own repository (embedded in the
                        // pages Lambda), never from user input.
                        <div className="rich-text" dangerouslySetInnerHTML={{ __html: page.html }} />
                    )}
                </div>
            </div>
        </div>
    );
}
